import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { isNotNullOrUndefined } from '@utils/utils';
import { CompanyLocationRampWithOpeningHoursOutDTO } from '@api/ringil-api.v2';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { deferStampDataTestIdOnDescendantRef, toTestIdSlug } from '@utils/testUtils';

interface ComponentProps {
  value: string;
  onChange: (value: string) => void;
  companyLocationId: string;
  companyId: string;
  showTemporaryClosed?: boolean;
  // Called with the ramps of the selected location once loaded — for callers that need to pick a
  // default or read a ramp's timezone without loading the list a second time.
  onRampsLoaded?: (ramps: CompanyLocationRampWithOpeningHoursOutDTO[]) => void;
  dataTestId?: string;
  style?: React.CSSProperties;
}

const InputCompanyRamp: React.FC<ComponentProps> = ({ value, onChange, companyLocationId, companyId, showTemporaryClosed = false, onRampsLoaded, dataTestId, style }) => {
  const { getSharedRampsForCompanyLocation } = useContext(ApiContext);
  const [data, setData] = useState<CompanyLocationRampWithOpeningHoursOutDTO[]>([]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return deferStampDataTestIdOnDescendantRef(wrapperRef, '.p-dropdown', dataTestId);
  }, [dataTestId, data]);

  useEffect(() => {
    const ab = new AbortController();
    if (isNotNullOrUndefined(companyLocationId)) {
      getSharedRampsForCompanyLocation(companyLocationId, companyId, showTemporaryClosed, d => {
        if (!ab.signal.aborted) {
          setData(d);
          onRampsLoaded?.(d);
        }
      });
    }
    return () => {
      ab.abort();
    };
  }, [companyLocationId]);

  return (
    <div ref={wrapperRef} style={style}>
      <Dropdown
        options={data}
        style={{ width: '100%' }}
        optionLabel={'name'}
        optionValue={'rampId'}
        onChange={e => onChange(e.value)}
        value={value}
        itemTemplate={(option: CompanyLocationRampWithOpeningHoursOutDTO) => (
          <span data-testid={dataTestId ? `${dataTestId}-${toTestIdSlug(option?.name || '')}` : undefined}>{option?.name}</span>
        )}
      />
    </div>
  );
};

export default InputCompanyRamp;
